import type { UploadOptions } from '..'
import { uuid } from '../../util/uuid'
import getInitialImageWidth from './getInitialImageWidth'

/**
 * Real file extensions for video MIME subtypes, used for the stored object's key.
 *
 * `fileType` is the MIME **subtype**, so without this a `.mov` is stored as `<id>.quicktime` and an
 * `.m4v` as `<id>.x-m4v` — extensions no player, tool or human recognises.
 *
 * That was cosmetic until #22523 made it load-bearing. The video transcode writes H.264 back over the
 * *same* key, and MediaConvert names a file output from its container, so it can only ever emit `.mp4`
 * or `.mov`. A canonical key ending `.quicktime` is therefore impossible to write back to: the output
 * lands on a `.mp4` sibling, the document keeps pointing at the untouched HEVC object, and the video
 * stays black in Chrome with nothing logging a problem.
 *
 * `m4v` maps to `mp4` rather than `m4v` for the same reason — MediaConvert has no `.m4v` container, and
 * `.m4v` is MP4 anyway.
 *
 * Only the stored key changes. The returned `fileType` is untouched, because callers use it as a
 * content type and to pick a follow-up task.
 */
const VIDEO_KEY_EXTENSIONS: Record<string, string> = {
  quicktime: 'mov',
  'x-m4v': 'mp4',
  m4v: 'mp4'
}

// Get key to store file under in image bucket
// Images, Mp4, Pdf go to uploads to be transformed,
// All else go right to company file
// If docId present, nest under that too
// Return clean fileType too
const getFileKey = (
  file: File,
  opts: UploadOptions
): { fileKey: string; fileType: File['type']; initialImageWidth: number } => {
  let fileType = file.type.split('/')[1]
  const id = uuid().slice(0, 8)
  const initialImageWidth = getInitialImageWidth()

  let base: string
  let subType: string
  let docId: string

  if (opts?.docId) {
    docId = `/doc-${opts.docId}`
  } else {
    docId = ''
  }

  switch (fileType) {
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'webp':
    case 'gif':
    case 'heic':
    case 'heif':
      subType = 'images'
      base = `${opts.companyId}${docId}/${subType}/${id}/${initialImageWidth}`
      break

    case 'svg+xml':
      subType = 'images'
      if (fileType === 'svg+xml') {
        fileType = 'svg'
      }
      base = `${opts.companyId}${docId}/${subType}/${id}/svg`
      break

    case 'pdf':
      subType = 'pdf'
      // base = `uploads/${opts.companyId}${docId}/${subType}/${id}`
      base = `${opts.companyId}${docId}/${subType}/${id}` // Try going right to instead, will generate pages on other end
      break

    case 'mp4':
      subType = 'mp4'
      base = `${opts.companyId}${docId}/${subType}/${id}`
      break

    default:
      base = `${opts.companyId}${docId}/${id}`
      break
  }
  const fileKey = base + `/${id}.${VIDEO_KEY_EXTENSIONS[fileType] ?? fileType}`

  return { fileKey, fileType, initialImageWidth }
}

export default getFileKey
